import React, { useState, useEffect, useMemo } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  CircularProgress,
  InputAdornment,
  IconButton,
} from '@mui/material';
import { validateEmail } from '../../../utils';
import { Visibility as VisibilityIcon, VisibilityOff as VisibilityOffIcon } from '@mui/icons-material';

function AddUserModal({ open, onClose, onSave, uid, isEdit, userToEdit }) {
 
  const initialUser = useMemo(() => {
    return isEdit
      ? userToEdit
      : {
        firstName: '',
        lastName: '',
        email: '',
        birthday: '',
        password: '',
        confirmPassword: '',
        universityUid: uid,
        };
  }, [isEdit, userToEdit, uid]);


  const [user, setUser] = useState(initialUser);

  const [validationErrors, setValidationErrors] = useState({
    firstName: '',
    lastName: '',
    email: '',
    birthday: '',
    password: '',
    confirmPassword: '',
  });

  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setUser(isEdit ? userToEdit : initialUser);
  }, [isEdit, userToEdit, initialUser]);
 
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setUser({
      ...user,
      [name]: value,
    });
  };

  const handleDateInputChange = (e) => {
    let { name, value } = e.target;
  
    // Remove any non-digit characters
    value = value.replace(/\D/g, '');
  
    // Ensure the day part does not exceed 31
    if (value.length >= 2) {
      let day = value.substring(0, 2);
      if (parseInt(day, 10) > 31) {
        day = '31';
      }
      value = day + value.substring(2);
    }
  
    // Ensure the month part does not exceed 12
    if (value.length >= 4) {
      let month = value.substring(2, 4);
      if (parseInt(month, 10) > 12) {
        month = '12';
      }
      value = value.substring(0, 2) + month + value.substring(4);
    }
  
    // Add a slash after the day when the user starts typing the next number
    if (value.length >= 3 && value.charAt(2) !== '/') {
      value = value.substring(0, 2) + '/' + value.substring(2);
    }
  
    // Add slashes to format the date as DD/MM/YYYY
    if (value.length >= 6 && value.charAt(5) !== '/') {
      value = value.substring(0, 5) + '/' + value.substring(5);
    }
  
    setUser({
      ...user,
      [name]: value,
    });
  };
  

  const handleSave = () => {
    console.log("click to save  outside");
    const errors = {};
    let isValid = true;
    if (user.firstName.length === 0) {
      errors.firstName = 'Le prénom est requis';
      isValid = false;
    }
    if (user.lastName.length === 0) {
      errors.lastName = 'Le nom est requis';
      isValid = false;
    }
    if (user.email.length === 0) {
      errors.email = "L'e-mail est requis";
      isValid = false;
    } else if (!validateEmail(user.email)) {
      errors.email = "L'e-mail n'est pas valide";
      isValid = false;
    }
    if (user.birthday.length === 0) {
      errors.birthday = 'La date de naissance est requise';
      isValid = false;
    }

    if (!isEdit) {
      // Only validate passwords for new users (not for editing)
      if (user.password.length < 6) {
        errors.password =
          'Le mot de passe doit contenir au moins 6 caractères';
        isValid = false;
      }
      if (user.password !== user.confirmPassword) {
        errors.confirmPassword = 'Les mots de passe ne correspondent pas';
        isValid = false;
      }
    }

    if (!user.universityUid) {
      errors.universityUid = 'Veuillez sélectionner une université';
      isValid = false;
    }

    if (isValid) {
      setLoading(true);
      console.log("click to save ");
      console.log(user);
      onSave(user)
        .then(() => {
          setUser({
            firstName: '',
            lastName: '',
            email: '',
            birthday: '',
            password: '',
            confirmPassword: '',
            universityUid: uid,
          });
          onClose();
        })
        .catch((error) => {
          console.error(error);
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      setValidationErrors(errors);
    }
  };
  const [showPassword, setShowPassword] = useState(false);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs">
      <DialogTitle>
        {isEdit ? 'Modifier élève' : 'Ajouter un nouvel élève'}
      </DialogTitle>
      <DialogContent>
        <TextField
          required
          label="Prénom"
          name="firstName"
          fullWidth
          value={user.firstName}
          onChange={handleInputChange}
          margin="normal"
          error={!!validationErrors.firstName}
          helperText={validationErrors.firstName}
        />
        <TextField
          required
          label="Nom"
          name="lastName"
          fullWidth
          value={user.lastName}
          onChange={handleInputChange}
          margin="normal"
          error={!!validationErrors.lastName}
          helperText={validationErrors.lastName}
        />
        <TextField
          required
          label="E-mail"
          name="email"
          fullWidth
          value={user.email}
          onChange={handleInputChange}
          margin="normal"
          error={!!validationErrors.email}
          helperText={validationErrors.email}
          disabled={isEdit} 
        /> 
        <TextField
          required
          label="Date de naissance (DD/MM/YYYY)"
          name="birthday"
          fullWidth
          value={user.birthday}
          onChange={handleDateInputChange}
          margin="normal"
          error={!!validationErrors.birthday}
          helperText={validationErrors.birthday}
          inputProps={{
            pattern: '^(0[1-9]|[12][0-9]|3[01])/(0[1-9]|1[0-2])/(19|20)\\d\\d$',
          }}
        />
        {!isEdit && (
          <>
       <TextField
          required
          label="Mot de passe"
          name="password"
          fullWidth
          type={showPassword ? 'text' : 'password'}
          value={user.password}
          onChange={handleInputChange}
          margin="normal"
          error={!!validationErrors.password}
          helperText={validationErrors.password}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={() => setShowPassword(!showPassword)}
                  edge="end"
                >
                  {showPassword ? <VisibilityIcon /> : <VisibilityOffIcon />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        <TextField
          required
          label="Confirmer le mot de passe"
          name="confirmPassword"
          fullWidth
          type={showPassword ? 'text' : 'password'}
          value={user.confirmPassword}
          onChange={handleInputChange}
          margin="normal"
          error={!!validationErrors.confirmPassword}
          helperText={validationErrors.confirmPassword}
        />

          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary">
          Annuler
        </Button>
        <Button onClick={handleSave} color="primary">
          {loading ? (
            <CircularProgress size={24} color="primary" />
          ) : (
            'Enregistrer'
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default AddUserModal;

